import { assertNever } from "@impero-com/common/utils/obj";

import {
    AdminEditAccessRights,
    AdminEditViewAccessRights,
    StandardAccessRight,
    UserOverview,
} from "api/generated/model";

import { ACCESS_RIGHTS } from "./constants";
import { UserAndRights, TypedAccessRight, AllRights } from "./models";

function convertUserAndRightsToAdminEditAccessRights(
    userAndRights: Array<
        UserAndRights<TypedAccessRight<any, Readonly<["Admin", "Edit"]>>>
    >
): AdminEditAccessRights {
    const accessRights: AdminEditAccessRights = {
        admin: [],
        edit: [],
    };

    userAndRights.forEach(
        (
            userAndRight: UserAndRights<
                TypedAccessRight<any, Readonly<["Admin", "Edit"]>>
            >
        ) => {
            switch (userAndRight.accessRight) {
                case "Admin":
                    accessRights.admin.push(userAndRight.user.id);
                    break;
                case "Edit":
                    accessRights.edit.push(userAndRight.user.id);
                    break;
                default:
                    assertNever(userAndRight.accessRight);
            }
        }
    );

    return accessRights;
}

type AccessRight = (typeof ACCESS_RIGHTS)[keyof typeof ACCESS_RIGHTS];
function convertUsersAndRightsToAdminEditViewAccessRights(
    usersAndRights: Array<UserAndRights<TypedAccessRight<any, AllRights>>>
): AdminEditViewAccessRights {
    const getRights = (right: AccessRight) =>
        usersAndRights
            .filter((usersAndRight) => usersAndRight.accessRight === right)
            .map((usersAndRight) => usersAndRight.user.id);
    return {
        admin: getRights(ACCESS_RIGHTS.ADMIN),
        edit: getRights(ACCESS_RIGHTS.EDIT),
        view: getRights(ACCESS_RIGHTS.VIEW),
    };
}

function convertAdminEditViewAccessRightsToUserAndRights(
    users: Array<UserOverview>,
    accessRights: AdminEditViewAccessRights
): Array<UserAndRights<TypedAccessRight<any, AllRights>>> {
    const { admin, edit, view } = accessRights;
    const usersAndRights: Array<
        UserAndRights<TypedAccessRight<any, AllRights>>
    > = [];

    const pushAccessRights = (
        userIds: Array<number>,
        accessRightType: StandardAccessRight
    ) => {
        userIds.forEach((userId) => {
            const user = users.find((user) => user.id === userId);
            if (user) {
                usersAndRights.push({
                    user,
                    accessRight: accessRightType,
                });
            }
        });
    };
    pushAccessRights(admin, ACCESS_RIGHTS.ADMIN);
    pushAccessRights(edit, ACCESS_RIGHTS.EDIT);
    pushAccessRights(view, ACCESS_RIGHTS.VIEW);

    return usersAndRights;
}

export {
    convertUserAndRightsToAdminEditAccessRights,
    convertUsersAndRightsToAdminEditViewAccessRights,
    convertAdminEditViewAccessRightsToUserAndRights,
};
